/**
 * Hook `useAuth` — TH-53154 / M1.E4.S1.3.
 *
 * Lê o usuário atual de GET /api/auth/me. Cookies HttpOnly viajam
 * sozinhos (credentials: include); sem token em localStorage.
 *
 * Estados:
 * - `user`: dados do usuário, ou `null` se não autenticado.
 * - `isLoading`: true enquanto a primeira chamada está em flight.
 * - `isAuthenticated`: derivado de `user !== null`.
 */

import { useQuery } from "@tanstack/react-query";

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  role:
    | "super_admin"
    | "tenant_admin"
    | "store_manager"
    | "operator"
    | "catalog_operator"
    | "salesperson";
  tenant_id: string | null;
  store_id: string | null;
}

async function fetchMe(): Promise<CurrentUser | null> {
  const res = await fetch("/api/auth/me", {
    credentials: "include",
  });
  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as CurrentUser;
}

export function useAuth() {
  const query = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000, // 5 min — re-validar quando voltar à aba
    retry: false, // 401 é resposta legítima, não erro a retentar
  });

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isAuthenticated: query.data != null,
    error: query.error,
  };
}
